import React, { useContext, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';
import Menu from '@material-ui/core/Menu';
import LaunchIcon from '@material-ui/icons/Launch';
import {
  ApiFilterOperation,
  Params,
  PromptContext,
  PromptProvider,
  ResultDto,
  SnackbarContext,
  SnackbarVariant,
  TableField,
  useFetch,
} from '@eas/common-web';
import { TableFieldProps } from '@eas/common-web/dist/types/components/table-field/table-field-types';
import { workOrderBulkApi } from '@modules/planner/calendar/controls/bulk-operations/bulk-operations-api';
import { ChangeStateMenuList } from '@modules/planner/work-order-menu/menu-items/state';
import {
  useRegisterUnplanWarningPrompt,
  useWorkOrderStateColumn,
} from '@modules/work-order/work-order-utils';
import { WorkOrder } from '@models';
import { EvidenceApiUrl } from '@enums';
import { Multitask } from './types';

const useColumns = ({
  afterSubmit,
}: {
  afterSubmit: () => void;
}): TableFieldProps<WorkOrder>['columns'] => [
  { datakey: 'workOrderId', name: 'Číslo zákazky', width: 230 },

  {
    datakey: 'locked',
    name: 'Uzamknutá',
    width: 80,
    CellComponent: ({ value }) => {
      return (
        <div
          style={{
            display: 'flex',
            gap: 16,
            margin: '4px 0',
            alignItems: 'center',
          }}
        >
          {value ? 'Áno' : 'Nie'}
        </div>
      );
    },
  },
  {
    datakey: 'currentState',
    name: 'Stav',
    width: 100,
    CellComponent: function ({ value }) {
      return useWorkOrderStateColumn(value);
    },
  },
  {
    datakey: '_changeState',
    name: 'Zmeniť stav',
    width: 100,
    CellComponent: ({ rowValue }) => {
      const ref = useRef<HTMLButtonElement>(null);

      const closeMenu = () => null;
      const [open, setOpen] = useState(false);

      return (
        <div
          style={{
            display: 'flex',
            gap: 16,
            margin: '4px 0',
            alignItems: 'center',
          }}
        >
          <Button
            style={{ fontSize: '11px', padding: '4px' }}
            ref={ref}
            onClick={() => setOpen(!open)}
            variant="contained"
            disabled={rowValue.locked}
          >
            Zmeniť stav
          </Button>
          <Menu
            anchorEl={ref.current}
            open={open}
            onClose={() => setOpen(false)}
          >
            <ChangeStateMenuList
              workOrder={rowValue}
              setOpen={setOpen}
              closeMenu={closeMenu}
              afterSubmit={afterSubmit}
            />
          </Menu>
        </div>
      );
    },
  },
  {
    datakey: 'id',
    name: 'Detail',
    width: 80,
    CellComponent: ({ value }) => (
      <Button
        onClick={() => window.open(`/zsd/pracovne-prikazy/${value}`, '_blank')}
      >
        <LaunchIcon fontSize="small" />
      </Button>
    ),
  },
];

const handleBulkLockWorkOrder = async ({
  ids,
  operation,
  afterSubmit,
  multitask,
  showSnackbar,
}: {
  ids: string[];
  operation: 'lock' | 'unlock';
  afterSubmit: () => void;
  multitask: Multitask;
  showSnackbar: SnackbarContext['showSnackbar'];
}) => {
  try {
    const result = await workOrderBulkApi[operation](ids);
    const failed = result
      .filter((i) => !i.success)
      .map(
        (r) =>
          multitask?.workOrderRefs?.find?.((w) => w.id === r.id)?.workOrderId
      );
    if (failed.length > 0) {
      showSnackbar(
        `Niektoré prac. príkazy sa nepodarilo ${
          operation === 'lock' ? 'zamknúť' : 'odomknúť'
        }: ${failed.join(', ')}
        `,
        SnackbarVariant.WARNING
      );
    }
  } catch (error) {
    console.log(error);
  }

  afterSubmit();
};

function hasState(codes: string[], currentState: string | undefined) {
  return codes.includes(currentState || '');
}

type Props = { multitask: Multitask; refetch: () => void; unitName: string };

export function MultitaskDetailDialog(props: Props) {
  return (
    <PromptProvider>
      <MultitaskDetailDialogInner {...props} />
    </PromptProvider>
  );
}

const useWorkOrderList = (multitask: Multitask) => {
  const params: Params = {
    filters: [
      {
        field: 'id',
        operation: ApiFilterOperation.IN,
        values: multitask.workOrderRefs.map((x) => x.id),
      },
    ],
    size: -1,
  };
  const [result, loading] = useFetch<ResultDto<WorkOrder>>(
    `${EvidenceApiUrl.WORK_ORDERS}/list`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify(params),
    }
  );
  return { result, loading };
};

function MultitaskDetailDialogInner({ multitask, refetch }: Props) {
  const columns = useColumns({ afterSubmit: refetch });

  const { result, loading } = useWorkOrderList(multitask);
  const { showSnackbar } = useContext(SnackbarContext);

  const handleUnplan = async (ids: string[] = []) => {
    try {
      const response = await workOrderBulkApi.unplan(ids.filter((i) => i));
      const failed = response
        .filter((i) => !i.success)
        .map(
          (r) => multitask.workOrderRefs.find((w) => w.id === r.id)?.workOrderId
        );

      if (failed.length > 0) {
        showSnackbar(
          `Niektoré prac. príkazy sa nepodarilo odplánovať: ${failed.join(', ')}
            `,
          SnackbarVariant.WARNING
        );
      }
    } catch (error) {
      console.log(error);
    }
    refetch();
  };

  const { testPrompt } = useContext(PromptContext);

  useRegisterUnplanWarningPrompt();

  const items = result?.items ?? multitask.workOrderRefs ?? [];

  return (
    <div>
      {loading && <LinearProgress />}
      <TableField
        columns={columns}
        onChange={() => {}}
        value={items}
        visibleEdit={false}
        visibleAdd={false}
        visibleRemove={false}
        visibleActionsColumn={true}
        showToolbar={true}
        showRadioCond={() => false}
        showCheckboxCond={() => true}
        ToolbarComponent={({ selectedIndexes }) => {
          const unplanDisabled = (selectedIndexes ?? []).some(
            (index) =>
              !hasState(['NAP', 'ODL', 'ODM'], items[index]?.currentState?.code)
          );
          return (
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'center',
                justifyContent: 'flex-end',
                margin: '8px 0',
              }}
            >
              <Button
                onClick={() => {
                  testPrompt({
                    key: 'WORK_ORDER_BULK_UNPLAN_ALERT',
                    submitCallback: async () => {
                      selectedIndexes &&
                        handleUnplan(
                          selectedIndexes.map((i) => items?.[i]?.id)
                        );
                    },
                  });
                }}
                variant="contained"
                color="primary"
                style={{ fontSize: '12px', padding: '4px 8px' }}
                disabled={
                  loading ||
                  !selectedIndexes ||
                  selectedIndexes.length === 0 ||
                  unplanDisabled
                }
              >
                Odplánovať označené
              </Button>
              <Button
                style={{ fontSize: '12px', padding: '4px 8px' }}
                color="primary"
                variant="contained"
                onClick={() =>
                  selectedIndexes &&
                  handleBulkLockWorkOrder({
                    ids: selectedIndexes.map((i) => items?.[i]?.id) ?? [],
                    operation: 'lock',
                    afterSubmit: refetch,
                    multitask,
                    showSnackbar,
                  })
                }
                disabled={
                  loading || !selectedIndexes || selectedIndexes.length === 0
                }
              >
                Zamknúť označené
              </Button>
              <Button
                style={{ fontSize: '12px', padding: '4px 8px' }}
                color="primary"
                variant="contained"
                onClick={() =>
                  selectedIndexes &&
                  handleBulkLockWorkOrder({
                    ids: selectedIndexes.map((i) => items?.[i]?.id) ?? [],
                    operation: 'unlock',
                    afterSubmit: refetch,
                    multitask,
                    showSnackbar,
                  })
                }
                disabled={
                  loading || !selectedIndexes || selectedIndexes.length === 0
                }
              >
                Odomknúť označené
              </Button>
            </div>
          );
        }}
      />
    </div>
  );
}
